import { getGenesysAgentCallbackAvailability } from 'api/supportAssistant'
import DatePickerInput from 'components/Common/Form/Input/DatePickerInput'
import InputLabelWrap from 'components/Common/Form/Input/InputLabelWrap'
import Select from 'components/Common/Form/Input/Select'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import type { ComponentProps } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

const TIME_ZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Hobart',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Perth',
  'Pacific/Auckland',
]

const NOW = moment()

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

interface Props {
  type: App.GenesysAgentCallbackType
}

interface CallbackTimeInterval {
  key: string
  start: string
}

function AgentCallbackRequestDateTimeInput({ type }: Props) {
  const intl = useIntl()
  const [date, setDate] = useState<string | undefined>(NOW.format(ISO_DATE_FORMAT))

  const [time, setTime] = useState<string>()
  const [timezone, setTimezone] = useState<string>(tz)
  const [filteredTimes, setFilteredTimes] = useState<Array<CallbackTimeInterval>>([])

  const handleDateChange = useCallback<NonNullable<ComponentProps<typeof DatePickerInput>['onDateChange']>>((_, isoDate) => {
    setDate(isoDate)
  }, [])

  const fetchAvailability = useCallback(async(timezone: string, date: string | undefined) => {
    return await getGenesysAgentCallbackAvailability({
      date,
      timezone,
    })
  }, [])

  useEffect(() => {
    const fetchAndSetIntervals = async() => {
      const availability = await fetchAvailability(timezone, date)
      const times = availability.data.times

      if (times.length === 0) {
        const tomorrowsDate = moment(NOW).add(1, 'day')
        setDate(tomorrowsDate.format(ISO_DATE_FORMAT))
      }

      setFilteredTimes(times)

      // We don't want to set the default time for these callback types
      if (type !== 'SUPPORT' && type !== 'SALES' && times.length > 0) {
        setTime(times[0].key)
      }
    }

    fetchAndSetIntervals()
  }, [date, fetchAvailability, timezone, type])

  const { minDate, maxDate } = useMemo(() => {
    // Genesys constraints only allow callbacks up to 30 days in the future
    const maxDate = new Date(NOW.year(), NOW.month(), NOW.date() + 30)

    const lastAvailableTime = filteredTimes.at(-1)?.key
    if (lastAvailableTime) {
      const [lastAvailableHour] = lastAvailableTime.split(':').map(Number)
      if (NOW.hour() >= lastAvailableHour) {
        const minDate = new Date(NOW.year(), NOW.month(), NOW.date() + 1)
        return { minDate, maxDate }
      }
    }

    const minDate = new Date(NOW.year(), NOW.month(), NOW.date())
    return { minDate, maxDate }
  }, [filteredTimes])

  return <Group direction="vertical" gap={8} horizontalAlign="stretch">
    <InputLabelWrap
      label={intl.formatMessage({
        id: 'r3XeMQJtyO',
        defaultMessage: 'Preferred callback date and time',
        description: 'Label for callback date and time input section',
      })}
      optionalIndicatorEnabled={false}
    >
      <Group direction="horizontal" gap={8} horizontalAlign="stretch">
        <DatePickerInput
          required
          dropdownPlacement="bottom"
          name="callbackDate"
          min={minDate}
          max={maxDate}
          value={date}
          onDateChange={handleDateChange}
          requiredErrorMessage={intl.formatMessage({
            id: 'XahJjVF263',
            defaultMessage: 'Please select date',
            description: 'Validation error when callback date is not selected',
          })}
        />
        <Select
          required
          invalidErrorMessage={intl.formatMessage({
            id: '7Dpoa8pGZI',
            defaultMessage: 'Please select time',
            description: 'Validation error when callback time is not selected',
          })}
          name="callbackTime"
          autoComplete="off"
          value={time}
          onChange={(e) => setTime(e.target.value)}
        >
          {filteredTimes.map((interval) => (
            <option key={interval.key} value={interval.start}>{interval.key}</option>
          ))}
        </Select>
      </Group>
      <Select
        required
        label={intl.formatMessage({
          id: 'F+8YH1HeL3',
          defaultMessage: 'Timezone',
          description: 'Label for timezone selection dropdown',
        })}
        invalidErrorMessage={intl.formatMessage({
          id: 'Ta6F2yP2g2',
          defaultMessage: 'Please select timezone',
          description: 'Validation error when timezone is not selected',
        })}
        name="timezone"
        autoComplete="off"
        value={timezone}
        onChange={(e) => setTimezone(e.target.value)}
      >
        {TIME_ZONES.map((timezone) => (
          <option key={timezone} value={timezone}>
            {timezone}
          </option>
        ))}
      </Select>
    </InputLabelWrap>
  </Group>
}

export default AgentCallbackRequestDateTimeInput
